import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FilterStateProps } from '@models/questions.model'
import { LinkManager } from '@utils/helpers'

import useQuery from './useQuery'

/**
 * Mantém a página atual (lida de `?page=`) sincronizada com os filtros da tela.
 *
 * Sempre que o conteúdo dos filtros muda, volta para a primeira página. Sem
 * isso, filtrar estando numa página alta manda para a API um OFFSET fora do
 * novo resultado e a listagem volta vazia — e como a paginação só é renderizada
 * quando há linhas, o usuário fica sem como voltar para a página 1.
 *
 * @param values filtros selecionados na tela
 *
 * @returns a página que deve ser enviada para a API
 *
 * @example
 * const [values, setValues] = useState<FilterStateProps>({ ... })
 * const page = useFilterPagination(values)
 */
export default function useFilterPagination(values: FilterStateProps) {
	const query = useQuery()
	const navigate = useNavigate()

	const pageQuery = Number(query.get('page')) || 1

	const [page, setPage] = useState(pageQuery)

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	/**
	 * A comparação é feita pelo conteúdo dos filtros, e não pela identidade de
	 * `values`: as telas recriam esse objeto nos efeitos em cascata de
	 * disciplina/assunto/subassunto durante a montagem, o que zeraria a página
	 * de quem abriu a tela direto numa URL com `?page=N`.
	 */
	const filterSignature = Object.values(values)
		.map((items) => items.map((value) => value.id).join(','))
		.join('|')

	const isFirstRender = useRef(true)

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false

			return
		}

		setPage(1)
		navigate(`/${LinkManager.replaceUrlParam('page', '1')}`, { replace: true })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterSignature])

	return page
}
